import { getMic } from "@/lib/mic";
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import MistOrb from "@/components/MistOrb";
import { computeEnvelope, envAt } from "@/lib/orbVoice";
import AdelaidaPortrait, { framesForVoice } from "@/components/AdelaidaPortrait";
import { PhoneOff, Loader2, Mic, X, Clock } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;


function splitSentences(text) {
  const t = (text || "").trim();
  if (!t) return [""];
  const parts = t.match(/[^.!?…]+[.!?…]+(?=\s|$)|[^.!?…]+$/g);
  if (!parts) return [t];
  const out = [];
  for (const p of parts.map((s) => s.trim()).filter(Boolean)) {
    if (out.length && out[out.length - 1].length < 18) out[out.length - 1] += " " + p;
    else out.push(p);
  }
  return out.length ? out : [t];
}

function LowTimeWarning({ warn, onChoose }) {
  const out = warn.left <= 0;
  const mins = Math.floor(warn.left / 60);
  const d = warn.refills ? new Date(warn.refills) : null;
  const monthly = warn.period === "month";
  const day = !d ? "soon" : monthly ? d.toLocaleDateString(undefined, { day: "numeric", month: "long" }) : d.toLocaleDateString(undefined, { weekday: "long" });
  return (
    <div className="absolute inset-0 z-30 flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-md p-5" data-testid="low-time-warning">
      <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-[#0d1426] p-6 text-white">
        <p className="text-[10px] uppercase tracking-[0.22em] text-amber-300 flex items-center gap-1.5"><Clock className="w-3 h-3" /> {out ? "Out of talk time" : "Running low"}</p>
        <p className="font-serif text-2xl leading-snug mt-2" data-testid="low-time-message">
          {out ? `You've used this ${monthly ? "month" : "week"}'s talk time.` : `You have ${mins < 1 ? "less than a minute" : `${mins} min`} of talk time left.`}
        </p>
        <p className="text-sm text-white/60 mt-2">{out ? `It refills on ${day}. Or top up now — top-up minutes never expire.` : `It refills on ${day}. Top up any time — top-up minutes never expire.`}</p>
        <div className="mt-6 flex flex-col gap-2">
          <button onClick={() => onChoose("topup")} className="w-full rounded-full bg-amber-400 text-black font-medium py-3 hover:bg-amber-300 transition-colors" data-testid="low-time-topup">Top up talk time</button>
          {out ? (
            <button onClick={() => onChoose(false)} className="w-full rounded-full border border-white/15 py-3 text-white/80 hover:bg-white/5 transition-colors" data-testid="low-time-close">Not now</button>
          ) : (
            <button onClick={() => onChoose(true)} className="w-full rounded-full border border-white/15 py-3 text-white/80 hover:bg-white/5 transition-colors" data-testid="low-time-continue">Start the call anyway</button>
          )}
        </div>
      </div>
    </div>
  );
}

// Compute a per-frame loudness envelope (RMS, ~40ms bins) from decoded audio,
// normalised 0..1. Used to drive the mouth from her ACTUAL voice (real sync).
// Hands-free voice loop. Visual is the free smoky orb by default; paid tiers can
// toggle a client-side photo-real Adelaida face that lip-syncs in-browser (£0/min — no streaming-avatar credits).
export default function VoiceCall({ title = "Xcelio", openingText, voice = "sage", sessionKind = "talk", autoAvatar = false, canUseAvatar = false, sendMessage, onExchange, onEnd }) {
  const [status, setStatus] = useState("connecting");
  const [caption, setCaption] = useState("");
  const [heard, setHeard] = useState(false);
  const [secsLeft, setSecsLeft] = useState(null);
  const [lowWarn, setLowWarn] = useState(null);
  const lowResolveRef = useRef(null);
  const callIdRef = useRef((typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `call_${Date.now()}`);

  const activeRef = useRef(true);
  const audioRef = useRef(null);
  const streamRef = useRef(null);
  const ctxRef = useRef(null);
  const analyserRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const speechRef = useRef(false);
  const heardRef = useRef(false);
  const speakTimerRef = useRef(null);
  // free client-side Adelaida face (photo-real 2D lip-sync)
  const canFaceRef = useRef(canUseAvatar);
  const [showFace, setShowFace] = useState(false);
  const showFaceRef = useRef(false);
  const amplitudeRef = useRef(0);
  const speakingRef = useRef(false);
  const ttsPlayerRef = useRef(null); // single persistent element, unlocked in the start gesture (reliable iOS audio)

  useEffect(() => {
    activeRef.current = true;
    canFaceRef.current = canUseAvatar;
    init();
    return () => { activeRef.current = false; cleanup(true); };
    // eslint-disable-next-line
  }, []);

  // Mouth is driven by her ACTUAL voice during playback (see _playAudio). When idle,
  // gently decay toward closed.
  useEffect(() => {
    let raf;
    const tick = () => {
      if (!speakingRef.current) amplitudeRef.current += (0 - amplitudeRef.current) * 0.15;
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const init = async () => {
    try {
      streamRef.current = await getMic({ echoCancellation: true, noiseSuppression: true, autoGainControl: true });
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      ctxRef.current = ctx;
      const analyser = ctx.createAnalyser(); analyser.fftSize = 512;
      ctx.createMediaStreamSource(streamRef.current).connect(analyser);
      analyserRef.current = analyser;
    } catch {
      onEnd(); return;
    }
    try {
      const { data } = await api.get("/usage");
      setSecsLeft(data.talk_remaining_seconds);
      const left = data.talk_remaining_seconds;
      if (left < 300) {
        const go = await new Promise((resolve) => { lowResolveRef.current = resolve; setLowWarn({ left, refills: data.talk_refills_at, period: data.talk_period }); });
        setLowWarn(null);
        if (go !== true || !activeRef.current) { if (go === "topup") goTopUp(); else end(); return; }
      }
    } catch {}
    // Paid tiers default to the free in-browser face; everyone else stays on the orb.
    if (autoAvatar && canFaceRef.current) { showFaceRef.current = true; setShowFace(true); }
    // Unlock a single persistent audio element now (nearest point to the tap) so every
    // TTS chunk plays reliably on iOS — creating a fresh Audio() per chunk gets blocked.
    try {
      const el = ttsPlayerRef.current;
      if (el) {
        el.muted = false; el.volume = 1;
        el.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=";
        await el.play().catch(() => {});
        try { el.pause(); el.currentTime = 0; } catch (e) {}
      }
    } catch (e) {}
    speak(openingText || "Hey. I'm here — what's going on for you?");
  };

  const goTopUp = () => { try { cleanup(true); } catch {} window.location.href = "/topup"; };

  // flip between the free orb and the free photo-real face (paid tiers only)
  const toggleFace = () => {
    if (!canFaceRef.current) return;
    const next = !showFaceRef.current;
    showFaceRef.current = next;
    setShowFace(next);
  };

  const cleanup = (full) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (speakTimerRef.current) clearTimeout(speakTimerRef.current);
    try { audioRef.current?.pause(); } catch {}
    try { ttsPlayerRef.current?.pause(); } catch {}
    try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch {}
    if (full) {
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
      try { if (ctxRef.current && ctxRef.current.state !== "closed") ctxRef.current.close().catch(() => {}); ctxRef.current = null; } catch {}
    }
  };

  const _synth = async (sentence) => {
    const { data } = await api.post("/tts", { text: sentence, voice, premium: false, session_id: callIdRef.current, session_kind: sessionKind });
    if (typeof data.talk_remaining_seconds === "number") setSecsLeft(data.talk_remaining_seconds);
    if (data.talk_limited || !data.url) return { limited: true };
    const url = `${BACKEND_URL}${data.url}`;
    let blobUrl = url, env = null;
    try {
      const resp = await fetch(url);
      const buf = await resp.arrayBuffer();
      blobUrl = URL.createObjectURL(new Blob([buf], { type: "audio/mpeg" }));
      if (ctxRef.current) { try { env = computeEnvelope(await ctxRef.current.decodeAudioData(buf.slice(0))); } catch (e) {} }
    } catch (e) { /* fall back to streaming the URL with no envelope */ }
    return { blobUrl, env };
  };

  const _playAudio = (res) => new Promise((resolve) => {
    const el = ttsPlayerRef.current;
    if (!el) return resolve();
    speakingRef.current = true;
    const env = res.env;
    let rafDrive = null;
    const cleanupDrive = () => {
      if (rafDrive) cancelAnimationFrame(rafDrive);
      if (res.blobUrl && res.blobUrl.startsWith("blob:")) { try { URL.revokeObjectURL(res.blobUrl); } catch (e) {} }
    };
    const done = () => { speakingRef.current = false; amplitudeRef.current = 0; cleanupDrive(); el.onended = null; el.onerror = null; resolve(); };
    el.onended = done; el.onerror = done;
    const drive = () => {
      if (el.paused || el.ended) return;
      // no envelope (decode failed) → keep the lips still rather than fake movement
      amplitudeRef.current = Math.pow(Math.max(0, Math.min(1, envAt(env, el.currentTime))), 0.7);
      rafDrive = requestAnimationFrame(drive);
    };
    try { el.src = res.blobUrl; el.currentTime = 0; } catch (e) {}
    el.play().then(() => { rafDrive = requestAnimationFrame(drive); }).catch(() => done());
  });

  // speak with sentence-level streaming: start talking after the first sentence
  // while the rest is synthesised in parallel — much faster time-to-first-word.
  const speak = async (text) => {
    if (!activeRef.current) return;
    setStatus("speaking"); setCaption(text); setHeard(false); heardRef.current = false;
    const sentences = splitSentences(text);
    let next = _synth(sentences[0]).catch(() => ({ error: true }));
    for (let i = 0; i < sentences.length; i++) {
      if (!activeRef.current) return;
      const res = await next;
      next = i + 1 < sentences.length ? _synth(sentences[i + 1]).catch(() => ({ error: true })) : null;
      if (res?.limited) {
        toast.error("You're out of talk time for now — top up any time.", {
          duration: 7000,
          action: { label: "Get more", onClick: () => { window.location.href = "/topup"; } },
        });
        end(); return;
      }
      if (res?.error || !res?.blobUrl) continue;
      if (!activeRef.current) return;
      try { await _playAudio(res); }
      catch (e) { console.warn("[speak] chunk failed:", e?.message || e); }
    }
    speakingRef.current = false;
    if (activeRef.current) setTimeout(startListening, 250);
  };

  const startListening = () => {
    if (!activeRef.current || !streamRef.current) return;
    if (speakTimerRef.current) clearTimeout(speakTimerRef.current);
    setStatus("listening"); setCaption(""); setHeard(false);
    speechRef.current = false; heardRef.current = false; chunksRef.current = [];
    try {
      const mr = new MediaRecorder(streamRef.current);
      recorderRef.current = mr;
      mr.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
      mr.onstop = () => handleStop();
      mr.start();
      runVAD();
    } catch { toast.error("Couldn't start listening. Tap the circle to talk."); setStatus("listening"); }
  };

  const runVAD = () => {
    const analyser = analyserRef.current;
    if (!analyser) return;
    const data = new Uint8Array(analyser.frequencyBinCount);
    const startedAt = Date.now();
    const CALIBRATE_MS = 500, SILENCE_MS = 1200, MAX_MS = 30000;
    let floor = 0, floorSamples = 0, speaking = false, silenceStart = null;
    const tick = () => {
      if (!activeRef.current || recorderRef.current?.state !== "recording") return;
      analyser.getByteTimeDomainData(data);
      let sum = 0; for (let i = 0; i < data.length; i++) { const v = data[i] - 128; sum += v * v; }
      const rms = Math.sqrt(sum / data.length); const now = Date.now(); const elapsed = now - startedAt;
      if (elapsed < CALIBRATE_MS) { floor += rms; floorSamples++; rafRef.current = requestAnimationFrame(tick); return; }
      const base = floorSamples ? floor / floorSamples : 3;
      const threshold = Math.max(base * 1.8 + 2, 6);
      if (rms > threshold) { speaking = true; silenceStart = null; if (!heardRef.current) { heardRef.current = true; setHeard(true); } }
      else if (speaking) {
        if (silenceStart == null) silenceStart = now;
        else if (now - silenceStart > SILENCE_MS) { speechRef.current = true; stopListening(); return; }
      }
      if (elapsed > MAX_MS) { speechRef.current = speaking; stopListening(); return; }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  };

  const stopListening = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    try { recorderRef.current?.state === "recording" && recorderRef.current.stop(); } catch {}
  };

  const tapSend = () => { if (status !== "listening") return; speechRef.current = true; stopListening(); };

  const handleStop = async () => {
    if (!activeRef.current) return;
    if (!speechRef.current && chunksRef.current.length === 0) { startListening(); return; }
    setStatus("thinking");
    const blob = new Blob(chunksRef.current, { type: "audio/webm" });
    if (blob.size < 1200) { setCaption("I didn't catch that — try again."); setTimeout(startListening, 600); return; }
    try {
      const fd = new FormData(); fd.append("file", blob, "audio.webm"); fd.append("session_id", callIdRef.current);
      const { data } = await api.post("/voice/transcribe", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const text = (data.text || "").trim();
      if (!text) { if (activeRef.current) { setCaption("I didn't catch that — try again."); setTimeout(startListening, 600); } return; }
      onExchange?.({ role: "user", text }); setCaption(text);
      const out = await sendMessage(text, callIdRef.current);
      if (!activeRef.current) return;
      const reply = typeof out === "string" ? out : out.text;
      onExchange?.({ role: "mirro", text: reply });
      if (out?.play?.url) playRecorded(reply, out.play);
      else speak(reply);
    } catch { if (activeRef.current) { toast.error("Lost you for a second — try again."); startListening(); } }
  };

  // she introduces it, then plays the recorded Reset in her voice (free — no talk time), lips moving with it
  const playRecorded = async (intro, play) => {
    setStatus("speaking"); setCaption(intro);
    const first = await _synth(intro).catch(() => null);
    if (first?.blobUrl && activeRef.current) { try { await _playAudio(first); } catch {} }
    if (!activeRef.current) return;
    setCaption(`${play.title} · breathe with me`);
    let blobUrl = `${BACKEND_URL}${play.url}`, env = null;
    try {
      const buf = await (await fetch(blobUrl)).arrayBuffer();
      blobUrl = URL.createObjectURL(new Blob([buf], { type: "audio/mpeg" }));
      // lips follow her voice only (the music bed underneath would keep the mouth moving)
      const vbuf = play.voice_url ? await (await fetch(`${BACKEND_URL}${play.voice_url}`)).arrayBuffer() : buf;
      if (ctxRef.current) { try { env = computeEnvelope(await ctxRef.current.decodeAudioData(vbuf.slice(0))); } catch {} }
    } catch {}
    if (!activeRef.current) return;
    try { await _playAudio({ blobUrl, env: env || { bins: [0], binMs: 1e9 } }); } catch {}
    speakingRef.current = false;
    if (!activeRef.current) return;
    const back = "Welcome back. How do you feel now?";
    api.post("/talk/reset-done").catch(() => {});
    onExchange?.({ role: "mirro", text: back });
    speak(back);
  };

  const end = () => { activeRef.current = false; cleanup(true); onEnd(); };

  const statusText = {
    connecting: "Connecting…", speaking: `${title} is speaking`,
    listening: heard ? "I hear you — keep going" : "Listening — just talk", thinking: "Thinking…",
  }[status];

  return (
    <div className="fixed inset-0 z-[60] overflow-hidden" style={{ background: "radial-gradient(120% 90% at 50% 30%, #10182e 0%, #0a0f1e 55%, #060912 100%)" }} data-testid="voice-call-overlay">
      {/* full-screen visual: photo-real Adelaida face when on, otherwise the smoky orb */}
      <div className="absolute inset-0 flex items-center justify-center">
        {showFace && canUseAvatar ? (
          <AdelaidaPortrait amplitudeRef={amplitudeRef} speakingRef={speakingRef} frames={framesForVoice(voice)} status={status} />
        ) : (
          <MistOrb size={Math.min(window.innerWidth * 0.78, 380)} amplitudeRef={amplitudeRef} state={status === "connecting" ? "thinking" : status} testId="voice-orb" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80 pointer-events-none" />
      </div>

      {lowWarn && <LowTimeWarning warn={lowWarn} onChoose={(c) => lowResolveRef.current?.(c)} />}

      {/* top bar */}
      <div className="absolute top-0 inset-x-0 px-5 pt-6 flex items-center justify-between z-10">
        <button onClick={end} aria-label="Close" data-testid="voice-close-btn"
          className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center text-white/90 hover:bg-white/20 transition-colors">
          <X className="w-5 h-5" />
        </button>
        <div className="font-serif text-xl text-white/95 tracking-wide">{title}</div>
        <div className="min-w-[84px] flex justify-end">
          {secsLeft != null && (() => {
            const unlimited = secsLeft >= 100000000; // owner/comp = effectively infinite
            const low = !unlimited && secsLeft <= 180;
            const label = unlimited ? "Unlimited" : secsLeft >= 60 ? `${Math.floor(secsLeft / 60)} min` : "< 1 min";
            return (
              <button onClick={low ? goTopUp : undefined} data-testid="voice-mins-left"
                className={`px-3 py-1.5 rounded-full text-xs font-medium backdrop-blur-md flex items-center gap-1.5 transition-colors ${low ? "bg-amber-500/25 text-amber-200 border border-amber-400/50" : "bg-white/10 text-white/75 border border-white/10"}`}>
                <Clock className="w-3.5 h-3.5" /> {low ? `${label} · Top up` : label}
              </button>
            );
          })()}
        </div>
      </div>

      {/* status pill */}
      <div className="absolute top-20 inset-x-0 flex justify-center z-10">
        <div className={`px-3 py-1 rounded-full bg-black/30 backdrop-blur-md text-[11px] uppercase tracking-[0.2em] transition-colors ${status === "listening" && heard ? "text-primary" : "text-white/70"}`} data-testid="voice-call-status">
          {statusText}
        </div>
      </div>

      {/* captions */}
      <div className="absolute inset-x-0 bottom-56 px-8 z-10 flex justify-center">
        {caption && <p className="text-center text-white/90 max-w-lg text-base md:text-lg leading-relaxed line-clamp-3 drop-shadow-lg" data-testid="voice-caption">{caption}</p>}
      </div>

      {/* toggle between the free orb and the free face — paid tiers only */}
      {canUseAvatar && (
        <div className="absolute bottom-32 inset-x-0 flex flex-col items-center gap-2 z-10">
          <button onClick={toggleFace} data-testid="voice-face-toggle"
            className="text-xs px-4 py-2 rounded-full border border-white/20 text-white/70 bg-white/5 backdrop-blur-md hover:bg-white/10 transition-colors">
            {showFace ? "Switch to the orb" : `Show ${["onyx", "echo", "ash", "fable"].includes(voice) ? "Richard" : "Adelaida"}'s face`}
          </button>
        </div>
      )}

      {/* bottom controls */}
      <div className="absolute bottom-0 inset-x-0 pb-10 px-8 z-10 flex items-center justify-center gap-6">
        <button onClick={tapSend} disabled={status !== "listening"} aria-label="Tap when you're done speaking" data-testid="voice-mic-btn"
          className={`w-16 h-16 rounded-full flex items-center justify-center backdrop-blur-md border transition-all ${status === "listening" ? (heard ? "bg-primary/25 border-primary/60 scale-105" : "bg-white/10 border-white/30 animate-pulse") : "bg-white/5 border-white/15 opacity-50"}`}>
          {status === "thinking" ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : <Mic className="w-6 h-6 text-white" />}
        </button>
        <button onClick={end} aria-label="End call" data-testid="end-call-btn"
          className="w-16 h-16 rounded-full bg-red-500/90 hover:bg-red-500 flex items-center justify-center text-white shadow-lg transition-colors">
          <PhoneOff className="w-6 h-6" />
        </button>
      </div>

      {status === "listening" && (
        <p className="absolute bottom-28 inset-x-0 text-center text-[11px] text-white/50 z-10">Just talk — I'll know when you're done</p>
      )}

      <audio ref={ttsPlayerRef} playsInline preload="auto" className="hidden" />
    </div>
  );
}
